import React, { useCallback, useMemo, useState } from "react";
import { FORM_ERROR } from "final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { pick } from "lodash";
import { withTypes } from "react-final-form";
import { useQueryClient } from "react-query";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SortableTable from "components/SortableTable";
import useTrashablePromises from "hooks/useTrashablePromises";
import translateError from "utils/translateError";
import useGlobalContext from "hooks/globalData";
import DirtyFormSpy from "components/DirtyFormSpy";
import Field from "components/Field";
import { TableCellType } from "components/LazyTable";
import {
  SkuStudyField,
  ListResponseOfSkuStudyField,
  SkuStudyFieldRequest,
  ListRequestOfSkuStudyFieldFilter,
} from "services/KnowfullyAdminClient";
import SelectStudyField from "./SelectStudyField";
import { SnackbarMessageProps, Severity } from "types/common-types";
import {
  SkuStudyFieldWithId,
  SkuStudyFieldFormValues,
  SkuFormProps,
} from "types/sku-related-types";
import SnackbarMessage from "components/SnackbarMessage";
import { useStrictQuery } from "hooks/useStrictQuery";
import { withErrorBoundary } from "hocs/withErrorBoundary";

const { Form } = withTypes<SkuStudyFieldFormValues>();

const SkuStudyFields = ({
  sku,
  onDirty,
  onSkuUpdated,
  onClean,
}: SkuFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [deleting, setDeleting] = useState<SkuStudyField | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const DeleteButton = ({ value }: { value: SkuStudyField }) => {
    return (
      <Box textAlign="center">
        <IconButton size="small" onClick={() => setDeleting(value)}>
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const handleDelete = useCallback(() => {
    const id = deleting?.id;
    setDeleting(null);
    if (id)
      return registerPromise(skuService.deleteSkuStudyField(id))
        .then(() => {
          queryClient.invalidateQueries(["skuStudyFields", sku.id!]);
          queryClient.invalidateQueries(["skuById", sku.id!]);

          setSnackbarMessage(
            new SnackbarMessageProps(
              t({ id: "skuStudyFieldDeleted" }),
              Severity.Warning
            )
          );
        })
        .catch((err: any) => {
          console.log(err);
        });
  }, [deleting?.id, queryClient, registerPromise, sku.id, skuService, t]);

  const { data, isFetching } = useStrictQuery<ListResponseOfSkuStudyField>(
    ["skuStudyFields", sku.id!],
    () =>
      skuService.getSkuStudyFields(
        sku.id!,
        ListRequestOfSkuStudyFieldFilter.fromJS({
          sortField: "createdOn",
          sortDescending: true,
        })
      )
  );

  const skuStudyFields = useMemo(
    () => (data?.data ?? []) as SkuStudyFieldWithId[],
    [data?.data]
  );

  const init = useMemo<SkuStudyFieldFormValues>(
    () => ({
      skuId: pick(sku.id! ?? "", ["id"]),
      skuStudyFieldRequest: new SkuStudyFieldRequest(),
      studyFieldId: "",
      credits: 0,
    }),
    [sku]
  );

  const form = useMemo(() => {
    const cells: Array<TableCellType> = [
      {
        accessor: "studyField.name",
        align: "left",
        width: 0,
        type: "text",
        label: t({ id: "skuStudyFieldLabel" }),
      },
      {
        accessor: "credits",
        align: "left",
        width: 0,
        type: "text",
        label: t({ id: "credits" }),
      },
      {
        accessor: "value",
        align: "center",
        width: 1,
        type: "node",
        View: DeleteButton,
        label: "",
        noSort: true,
      },
    ];

    const onSubmit = async (values: SkuStudyFieldFormValues) => {
      const existingStudyField = skuStudyFields.find(
        (x) => x.studyField?.id! === values.studyFieldId!
      );

      if (existingStudyField !== undefined) {
        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "skuStudyFieldRepeated" }),
            Severity.Warning
          )
        );
        onSkuUpdated && onSkuUpdated();
        return values;
      }

      try {
        const response = await registerPromise(
          skuService.saveSkuStudyField(
            sku.id!,
            SkuStudyFieldRequest.fromJS(values)
          )
        );

        queryClient.invalidateQueries(["skuStudyFields", sku.id!]);
        setSnackbarMessage(
          new SnackbarMessageProps(t({ id: "skuStudyFieldCreated" }))
        );
        onSkuUpdated && onSkuUpdated(sku.id!);

        return response;
      } catch (err: any) {
        console.log(err);
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, dirty, values }) => {
          return (
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <DirtyFormSpy onDirty={onDirty} onClean={onClean} />

              <Box mb={3}>
                <Grid container spacing={1}>
                  <Grid item xs={9}>
                    <Field name="studyFieldId">
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectStudyField
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            onDirty={onDirty}
                            onClean={onClean}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={1}></Grid>
                  <Grid item xs={2}>
                    <Field name="credits" type="number">
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <TextField
                            variant="standard"
                            {...input}
                            label="Credits"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{ min: 0, step: ".01" }}
                            data-cy="creditsText"
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <Box textAlign="right">
                      <Button
                        variant="outlined"
                        type="submit"
                        color="primary"
                        disabled={submitting || !dirty || !values.studyFieldId}
                        data-cy="addStudyFieldButton"
                      >
                        <T id="skuStudyFieldCreateButtonLabel" />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <SortableTable
                {...{
                  rows: skuStudyFields,
                  loading: isFetching,
                  dense: true,
                  cells,
                }}
              />

              <Dialog open={!!deleting} disableEnforceFocus>
                <DialogTitle>
                  <T id="skuStudyFieldDeleteTitle" />
                </DialogTitle>
                <DialogContent dividers>
                  <T id="skuStudyFieldDeleteQuestion" />
                </DialogContent>
                <DialogActions>
                  <Button
                    autoFocus
                    onClick={() => setDeleting(null)}
                    color="primary"
                    data-cy="cancelButton"
                  >
                    <T id="cancelButton" />
                  </Button>
                  <Button
                    onClick={handleDelete}
                    color="secondary"
                    variant="contained"
                    data-cy="deleteButton"
                  >
                    <T id="deleteButton" />
                  </Button>
                </DialogActions>
              </Dialog>
              <SnackbarMessage
                snackbarMessage={snackbarMessage}
                setSnackbarMessage={setSnackbarMessage}
              />
            </form>
          );
        }}
      />
    );
  }, [
    t,
    init,
    skuStudyFields,
    sku.id,
    registerPromise,
    skuService,
    onSkuUpdated,
    queryClient,
    onDirty,
    onClean,
    isFetching,
    deleting,
    handleDelete,
    snackbarMessage,
  ]);

  return form;
};

export default withErrorBoundary(SkuStudyFields);
